import type { ReactNode } from 'react';

import type { ApiKey, ApiRequest } from '@/account/api-keys';
import { Button } from '@/components/Button/Button';
import { HttpStatus, KeyStatus } from '@/components/KeyStatus/KeyStatus';
import { formatDateTime, formatRelative } from '@/lib/format';

import styles from './ApiKeyDetail.module.css';

export interface ApiKeyDetailProps {
  apiKey: ApiKey;
  requests: ApiRequest[];
  hasMore?: boolean;
  loadingMore?: boolean;
  pingUrl: string;
  onLoadMore: () => void;
  onRefresh: () => void;
  onRegenerate: () => void;
  onRevoke: () => void;
  /** A link back to the list; routing is the caller's. */
  back?: ReactNode;
}

function Fact({ term, children }: { term: string; children: ReactNode }) {
  return (
    <div className={styles.fact}>
      <dt>{term}</dt>
      <dd>{children}</dd>
    </div>
  );
}

/** One key: what it is, what can be done to it, and what it has been used for. */
export function ApiKeyDetail({
  apiKey,
  requests,
  hasMore = false,
  loadingMore = false,
  pingUrl,
  onLoadMore,
  onRefresh,
  onRegenerate,
  onRevoke,
  back,
}: ApiKeyDetailProps) {
  const revoked = Boolean(apiKey.revokedAt);

  return (
    <div className={styles.page}>
      {back ? <div className={styles.back}>{back}</div> : null}

      <section className={styles.card} aria-labelledby="key-heading">
        <div className={styles.header}>
          <div className={styles.title}>
            <h2 id="key-heading" className={styles.heading}>
              {apiKey.name || 'Untitled key'}
            </h2>
            <KeyStatus revoked={revoked} />
          </div>
          {revoked ? null : (
            <div className={styles.actions}>
              <Button variant="ghost" onClick={onRegenerate}>
                Regenerate
              </Button>
              <Button variant="danger" onClick={onRevoke}>
                Revoke
              </Button>
            </div>
          )}
        </div>

        <dl className={styles.facts}>
          <Fact term="Key">
            <code className={styles.prefix}>{apiKey.secretPrefix}…</code>
          </Fact>
          <Fact term="Created">{apiKey.createdAt ? formatDateTime(apiKey.createdAt) : '—'}</Fact>
          <Fact term="Last used">
            {apiKey.lastUsedAt ? (
              <time dateTime={apiKey.lastUsedAt} title={formatDateTime(apiKey.lastUsedAt)}>
                {formatRelative(apiKey.lastUsedAt)}
              </time>
            ) : (
              'Never'
            )}
          </Fact>
          {revoked && apiKey.revokedAt ? (
            <Fact term="Revoked">{formatDateTime(apiKey.revokedAt)}</Fact>
          ) : null}
        </dl>
      </section>

      <section className={styles.card} aria-labelledby="requests-heading">
        <div className={styles.header}>
          <h3 id="requests-heading" className={styles.subheading}>
            Recent requests
          </h3>
          <Button variant="ghost" onClick={onRefresh} icon="↻">
            Refresh
          </Button>
        </div>

        {requests.length === 0 ? (
          <div className={styles.empty}>
            <p className={styles.emptyTitle}>No requests yet</p>
            <p>
              Every call made with this key appears here, refusals included. Try{' '}
              <code className={styles.prefix}>GET {pingUrl}</code>.
            </p>
          </div>
        ) : (
          <div className={styles.scroll}>
            <table className={styles.table} aria-label="Requests">
              <thead>
                <tr>
                  <th scope="col">Time</th>
                  <th scope="col">Request</th>
                  <th scope="col">Status</th>
                  <th scope="col">Latency</th>
                  <th scope="col">Request ID</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((request) => (
                  <tr key={request.id}>
                    <td className={styles.muted}>
                      {request.createdAt ? formatDateTime(request.createdAt) : '—'}
                    </td>
                    <td>
                      <code className={styles.path}>
                        <span className={styles.method}>{request.method}</span> {request.path}
                      </code>
                    </td>
                    <td>
                      <HttpStatus status={request.status} />
                      {request.errorCode ? (
                        <span className={styles.errorCode}>{request.errorCode}</span>
                      ) : null}
                    </td>
                    <td className={styles.muted}>
                      {request.latencyMs === null ? '—' : `${request.latencyMs} ms`}
                    </td>
                    <td>
                      <code className={styles.requestId}>{request.requestId}</code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {hasMore ? (
          <div className={styles.more}>
            <Button variant="ghost" onClick={onLoadMore} loading={loadingMore}>
              Load older requests
            </Button>
          </div>
        ) : null}
      </section>
    </div>
  );
}
